import api from '../api';

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    console.log('[Push] Push notifications not supported');
    return null;
  }

  try {
    // Check if already subscribed
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Already subscribed, just update server
      await sendSubscriptionToServer(subscription);
      return subscription;
    }

    // Get VAPID public key from server
    const { data } = await api.get('/push/vapid-public-key');
    if (!data.configured || !data.publicKey) {
      console.log('[Push] Push notifications not configured on server');
      return null;
    }

    // Request permission if not already granted
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('[Push] Notification permission denied');
      return null;
    }

    // Subscribe to push
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey)
    });

    // Send subscription to server
    await sendSubscriptionToServer(subscription);

    console.log('[Push] Successfully subscribed to push notifications');
    return subscription;
  } catch (error) {
    console.error('[Push] Failed to subscribe:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      await api.delete('/push/unsubscribe', { data: { endpoint: subscription.endpoint } });
      console.log('[Push] Successfully unsubscribed');
    }
  } catch (error) {
    console.error('[Push] Failed to unsubscribe:', error);
  }
}

/**
 * Send subscription to server
 */
async function sendSubscriptionToServer(subscription) {
  const subscriptionJson = subscription.toJSON();
  await api.post('/push/subscribe', {
    endpoint: subscriptionJson.endpoint,
    keys: subscriptionJson.keys
  });
}

/**
 * Convert VAPID public key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Get current subscription status
 */
export async function getPushStatus() {
  try {
    const { data } = await api.get('/push/status');
    return data;
  } catch (error) {
    return { configured: false, subscribed: false };
  }
}
